import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
} from "@mantine/core";
import { IconSearch } from "@tabler/icons-react";
import BottomSheet from "../../../common/BottomSheet";
import { FilterOption } from "../../../common/FilterChip";
import { ReportFilterDef } from "../../../../types/reportsV2";
import styles from "./MobileSheets.module.css";

/** Show a search field once a filter has more options than this. */
export const FILTER_SEARCH_THRESHOLD = 8;

export interface MobileFilterSheetProps {
  /** The filter being edited; null closes the sheet. */
  filter: ReportFilterDef | null;
  /** Its options (from the run's facets). */
  options: FilterOption[];
  selected: string[];
  onChange: (values: string[]) => void;
  /** Optional filters: "Remove filter" takes the chip off the toolbar. */
  onRemove?: () => void;
  onClose: () => void;
}

interface OptionsProps {
  label: string;
  options: FilterOption[];
  selected: string[];
  onToggle: (value: string) => void;
}

/** Search + checkbox rows; mounted per filter, so the search starts empty. */
function FilterOptions({ label, options, selected, onToggle }: OptionsProps) {
  const [query, setQuery] = useState("");
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q
      ? options.filter((o) => o.label.toLowerCase().includes(q))
      : options;
  }, [options, query]);

  return (
    <>
      {options.length > FILTER_SEARCH_THRESHOLD && (
        <TextInput
          mb="xs"
          value={query}
          onChange={(e) => setQuery(e.currentTarget.value)}
          placeholder={`Search ${label.toLowerCase()}`}
          aria-label={`Search ${label} options`}
          leftSection={<IconSearch size={15} aria-hidden />}
        />
      )}
      <Stack gap={0} role="group" aria-label={label}>
        {visible.map((option) => (
          <Checkbox
            key={option.value}
            className={styles.checkRow}
            label={option.label}
            checked={selected.includes(option.value)}
            onChange={() => onToggle(option.value)}
          />
        ))}
      </Stack>
      {visible.length === 0 && (
        <Text className={styles.empty}>
          {options.length === 0 ? "No options in this range" : "No matches"}
        </Text>
      )}
    </>
  );
}

/**
 * A filter's multi-select on a phone (the desktop chip menu): checkbox rows,
 * search for long lists, Clear, Remove filter for optional filters, Done.
 */
export default function MobileFilterSheet({
  filter,
  options,
  selected,
  onChange,
  onRemove,
  onClose,
}: MobileFilterSheetProps) {
  const label = filter?.label ?? "";
  const toggle = (value: string) =>
    onChange(
      selected.includes(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value],
    );

  return (
    <BottomSheet
      labelDialog
      opened={!!filter}
      onClose={onClose}
      title={label}
      data-testid="report-filter-sheet"
      footer={
        <>
          <UnstyledButton
            type="button"
            className={styles.linkButton}
            disabled={selected.length === 0}
            onClick={() => onChange([])}
          >
            Clear
          </UnstyledButton>
          {onRemove && (
            <UnstyledButton
              type="button"
              className={styles.linkButton}
              data-danger
              onClick={() => {
                onClose();
                onRemove();
              }}
            >
              Remove filter
            </UnstyledButton>
          )}
          <Box className={styles.spacer} />
          <Button color="teal" onClick={onClose}>
            Done
          </Button>
        </>
      }
    >
      {filter && (
        <FilterOptions
          key={filter.key}
          label={label}
          options={options}
          selected={selected}
          onToggle={toggle}
        />
      )}
    </BottomSheet>
  );
}
